import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveGroup, setCollapsed } from '@kss/layouts';
import navData from './sidebarNav.json';
import type { NavItem } from '../../types';
import IconRail from './IconRail';
import SubmenuPanel from './SubmenuPanel';
import { cn } from '../../utils/cn';

const staticNavItems = navData as NavItem[];

export interface SidebarProps {
  /**
   * Optional override of the nav tree. When provided AND non-empty,
   * replaces the static `sidebarNav.json` config — this is how the
   * panel app feeds in the dynamic menu fetched from /api/Menu/all
   * + /api/RoleMenu/all.
   *
   * - `undefined` → fall back to the static config (used by the UI
   *   showcase and by an admin whose tenant has no seeded RoleMenu
   *   rows yet, so they can still navigate on day one).
   * - `[]` + `emptyMessage` → render the empty-state panel instead of
   *   any nav. This is how the panel signals "the current non-admin
   *   user has no menus granted" without leaking the static tree.
   * - `[]` + no `emptyMessage` → same as `undefined` (fallback).
   * - non-empty array → render exactly these items.
   */
  items?: NavItem[];
  /**
   * When set AND `items` is an empty array, the sidebar renders this
   * message instead of nav items or the static fallback. Used to
   * surface "No menus have been assigned to your role — contact
   * admin" for non-admin users with no RoleMenu grants.
   */
  emptyMessage?: string;
}

export default function Sidebar({ items, emptyMessage }: SidebarProps = {}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { collapsed, activeGroupId } = useSelector((s: any) => s.sidebar);

  // Distinguishes the three cases described on `SidebarProps.items`:
  //   showEmpty        → non-admin user with no menus granted
  //   navItems (list)  → dynamic tree wins when non-empty
  //   navItems (fallback) → undefined/empty items + no message → static
  const showEmpty = !!emptyMessage && Array.isArray(items) && items.length === 0;
  const navItems = useMemo(
    () => (items && items.length > 0 ? items : staticNavItems),
    [items],
  );

  // Auto-select nav group based on current route. Skipped when we're
  // rendering the empty state — there's nothing to match against.
  useEffect(() => {
    if (showEmpty) return;
    const match = navItems.find(
      (item) =>
        item.children?.some((child) => location.pathname === child.path) ||
        item.path === location.pathname
    );
    if (match && match.id !== activeGroupId) {
      dispatch(setActiveGroup(match.id));
    }
  }, [location.pathname, navItems, showEmpty]);

  const handleGroupSelect = (id: string): void => {
    const item = navItems.find((n) => n.id === id);
    const hasChildren = !!item?.children?.length;

    if (!hasChildren) {
      // Leaf icon: IconRail handles navigation. Don't mutate the expanded
      // state — the topbar hamburger is the sole collapse control.
      return;
    }

    if (collapsed && hasChildren) {
      // Collapsed rail → expand submenu for this group
      dispatch(setCollapsed(false));
      dispatch(setActiveGroup(id));
      return;
    }

    if (!collapsed && id === activeGroupId && hasChildren) {
      // Same group clicked again while expanded → collapse back to icon rail
      dispatch(setCollapsed(true));
      return;
    }

    dispatch(setActiveGroup(id));
  };

  return (
    <aside className="no-print flex h-full flex-shrink-0 flex-col">
      {/* Unified logo spanning the sidebar (icon rail + submenu) */}
      <div
        className={cn(
          'flex h-14 shrink-0 items-center gap-2 border-b border-r border-border bg-surface-sidebar px-4',
          collapsed ? 'w-16 justify-center px-0' : 'w-[304px]'
        )}
      >
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
          K
        </span>
        {!collapsed && (
          <span className="text-sm font-bold text-content-primary">KSS</span>
        )}
      </div>

      <div className="flex min-h-0 flex-1">
        {showEmpty ? (
          // Empty state: keep the aside width consistent with the
          // normal expanded layout so the main content doesn't jump.
          <div
            className={cn(
              'flex h-full flex-col border-r border-border bg-surface-sidebar',
              collapsed ? 'w-16' : 'w-[304px]'
            )}
          >
            {!collapsed && (
              <div className="flex flex-1 flex-col items-center justify-start p-6 text-center">
                <span className="text-xs text-content-muted">{emptyMessage}</span>
              </div>
            )}
          </div>
        ) : (
          <>
            <IconRail
              items={navItems}
              activeGroupId={activeGroupId}
              collapsed={collapsed}
              onGroupSelect={handleGroupSelect}
            />
            {!collapsed && (
              <SubmenuPanel items={navItems} activeGroupId={activeGroupId} />
            )}
          </>
        )}
      </div>
    </aside>
  );
}
